import * as React from "react";
import Forbidden from "@app/routes/errors/Forbidden";

export type User = {
  id?: string;
  roles?: string[];
  permissions?: string[];
};

const ADMIN_ROLES = ["Admin", "TenantAdmin"];

// --- Role and Permission Helpers ---

export function isAdmin(user: User | null | undefined): boolean {
  return !!user?.roles?.some((r) => ADMIN_ROLES.includes(r));
}

export function hasPermission(
  user: User | null | undefined,
  permission: string,
) {
  if (isAdmin(user)) return true; // ✅ Admin bypass
  if (!user?.permissions) return false;
  return user.permissions.includes(permission);
}

// Effective grants match the API permission handler; tenant role names do not widen grants.
export function hasEffectivePermission(
  user: User | null | undefined,
  permission: string,
) {
  return !!(
    user?.permissions?.includes(permission) ||
    user?.roles?.some((role) => ["GlobalAdmin", "Superadmin"].includes(role))
  );
}

export function hasRole(
  user: User | null | undefined,
  required: string | string[],
) {
  if (isAdmin(user)) return true; // ✅ Admin bypass
  const roles = Array.isArray(required) ? required : [required];
  if (!user?.roles) return false;
  return roles.some((r) => user.roles!.includes(r));
}

// --- RBAC Context and Components ---

type Ctx = { user: User | null };
const RbacContext = React.createContext<Ctx>({ user: null });

export function RbacProvider({
  user,
  children,
}: {
  user: User | null;
  children: React.ReactNode;
}) {
  return (
    <RbacContext.Provider value={{ user }}>{children}</RbacContext.Provider>
  );
}

export function useRbac() {
  return React.useContext(RbacContext);
}

// --- Conditional Components ---

export function RequireRole({
  roles,
  children,
}: {
  roles: string[];
  children: React.ReactNode;
}) {
  const { user } = useRbac();
  if (!hasRole(user, roles)) return null;
  return <>{children}</>;
}

export function RequirePerm({
  perm,
  children,
}: {
  perm: string;
  children: React.ReactNode;
}) {
  const { user } = useRbac();
  if (!hasPermission(user, perm)) return <Forbidden />;
  return <>{children}</>;
}
